/**
 * `useMe()` — small wrapper around the existing `fetchMe` helper so all pages
 * (dashboard, agent detail, …) share one cache key and one source of truth
 * for the active tenant + user context (RLY-024).
 *
 * Standing order [04]: the API base URL is resolved through `resolveApiBaseUrl`
 * (the shared pure function from `@/lib/api-client`) which throws a
 * `MissingApiBaseUrlError` in non-DEV builds when `VITE_API_BASE_URL` is unset.
 * No silent localhost fallback past DEV — same constraint the api-client's
 * module-level resolution enforces.
 */
import { useAuth } from "@clerk/clerk-react";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { ApiError, Me } from "@/lib/api-client";
import { fetchMe, resolveApiBaseUrl } from "@/lib/api-client";

const apiBaseUrl = resolveApiBaseUrl({
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  DEV: import.meta.env.DEV,
});

export const meKeys = {
  current: ["me"] as const,
} as const;

export type MeQueryKey = typeof meKeys.current;
export type UseMeResult = UseQueryResult<Me, ApiError | Error>;

export function useMe(): UseMeResult {
  const { getToken } = useAuth();
  return useQuery<Me, ApiError | Error, Me, MeQueryKey>({
    queryKey: meKeys.current,
    queryFn: () => fetchMe(apiBaseUrl, () => getToken()),
  });
}
